import { Close } from '@mui/icons-material';
import { Avatar, Chip, ChipProps, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

export interface FilterChipProps {
    /** The chip label - can be string or ReactNode for custom content */
    label: ReactNode;
    /** Called when delete icon is clicked */
    onDelete?: () => void;
    /** Optional avatar (e.g., for assignee chips) */
    avatar?: {
        src?: string;
        alt?: string;
    };
    /** Optional start icon */
    icon?: ReactNode;
    /** Additional sx props to merge with defaults */
    sx?: SxProps<Theme>;
    /** Pass-through props for MUI Chip */
    chipProps?: Omit<ChipProps, 'label' | 'onDelete' | 'avatar' | 'icon' | 'sx'>;
}

/** Default styles for filter chips */
export const filterChipSx: SxProps<Theme> = {
    borderRadius: '8px',
    border: '1px solid #4239B0',
    opacity: '0.9',
    background: 'rgba(60, 45, 225, 0.30)',
    color: theme => theme.palette.secondary.main,
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '13px',
};

/** Default delete icon for filter chips */
export const FilterChipDeleteIcon = (props: any) => (
    <Close
        {...props}
        sx={{
            fill: theme => theme.palette.secondary.main,
            width: '15px',
            height: '14px',
        }}
    />
);

/**
 * Reusable filter chip component with consistent styling.
 * Supports avatars for assignee display and custom icons.
 */
export const FilterChip = ({
    label,
    onDelete,
    avatar,
    icon,
    sx,
    chipProps,
}: FilterChipProps) => {
    return (
        <Chip
            label={label}
            onDelete={onDelete}
            avatar={avatar ? <Avatar src={avatar.src} alt={avatar.alt} /> : undefined}
            icon={icon as any}
            deleteIcon={<FilterChipDeleteIcon />}
            variant="outlined"
            sx={{
                ...filterChipSx,
                ...(avatar && {
                    '& .MuiAvatar-root': {
                        width: 20,
                        height: 20,
                        marginLeft: '4px',
                    },
                }),
                ...sx,
            }}
            {...chipProps}
        />
    );
};

export default FilterChip;
